import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';

import { ValidationResultItem } from '../../typings/common';
import { MassActionId } from '../../typings/groupsEditState';
import { KeywordsEditorBodyView } from '../../typings/keywordsEditorData';
import { MinusPhrasesEditorTabs } from '../../typings/minusPhrases';
import { MinusPhraseLibraryEditorData, MinusPhraseLibraryEditorState } from '../../typings/state';
import { ServerErrorMapperCode } from '../ErrorsMapper/ErrorsMapper';

import * as keyset from './MinusPhraseService.i18n';

const i18n = i18nFactory(keyset);

export function getSuccessMinusPhraseBindingMessage(
    totalGroups: number,
    successGroupsCount: number,
    minusPhrasesListCount: number
): string {
    if (minusPhrasesListCount > 1) {
        if (successGroupsCount === totalGroups) {
            if (successGroupsCount > 1) {
                return i18n('minus-phrase-binding-many-phrases-list-success-many', {
                    count: successGroupsCount
                });
            } else {
                return i18n('minus-phrase-binding-many-phrases-list-success-one', {
                    count: successGroupsCount
                });
            }
        } else {
            return i18n('minus-phrase-binding-many-phrases-list-warning', {
                count: successGroupsCount,
                total: totalGroups
            });
        }
    } else {
        if (successGroupsCount === totalGroups) {
            if (successGroupsCount > 1) {
                return i18n('minus-phrase-binding-one-phrases-list-success-many', {
                    count: successGroupsCount
                });
            } else {
                return i18n('minus-phrase-binding-one-phrases-list-success-one', {
                    count: successGroupsCount
                });
            }
        } else {
            return i18n('minus-phrase-binding-one-phrases-list-warning', {
                count: successGroupsCount,
                total: totalGroups
            });
        }
    }
}

export function getErrorMinusPhraseBindingMessage(minusPhrasesListCount: number, totalGroups: number): string {
    if (minusPhrasesListCount > 1) {
        if (totalGroups > 1) {
            return i18n('minus-phrase-binding-many-phrases-list-error-many');
        } else {
            return i18n('minus-phrase-binding-many-phrases-list-error-one');
        }
    } else {
        if (totalGroups > 1) {
            return i18n('minus-phrase-binding-one-phrases-list-error-many');
        } else {
            return i18n('minus-phrase-binding-one-phrases-list-error-one');
        }
    }
}

export function getSuccessMinusPhraseUnbindingMessage(
    totalGroups: number,
    successGroupsCount: number,
    minusPhrasesListCount: number
): string {
    if (minusPhrasesListCount > 1) {
        if (successGroupsCount === totalGroups) {
            if (successGroupsCount > 1) {
                return i18n('minus-phrase-unbinding-many-phrases-list-success-many');
            } else {
                return i18n('minus-phrase-unbinding-many-phrases-list-success-one');
            }
        } else {
            return i18n('minus-phrase-unbinding-many-phrases-list-warning', {
                count: successGroupsCount,
                total: totalGroups
            });
        }
    } else {
        if (successGroupsCount === totalGroups) {
            if (successGroupsCount > 1) {
                return i18n('minus-phrase-unbinding-one-phrases-list-success-many');
            } else {
                return i18n('minus-phrase-unbinding-one-phrases-list-success-one');
            }
        } else {
            return i18n('minus-phrase-unbinding-one-phrases-list-warning', {
                count: successGroupsCount,
                total: totalGroups
            });
        }
    }
}

export function getErrorMinusPhraseUnbindingMessage(minusPhrasesListCount: number, totalGroups: number): string {
    if (minusPhrasesListCount > 1) {
        if (totalGroups > 1) {
            return i18n('minus-phrase-unbinding-many-phrases-list-error-many');
        } else {
            return i18n('minus-phrase-unbinding-many-phrases-list-error-one');
        }
    } else {
        if (totalGroups > 1) {
            return i18n('minus-phrase-unbinding-one-phrases-list-error-many');
        } else {
            return i18n('minus-phrase-unbinding-one-phrases-list-error-one');
        }
    }
}

export function getMinusPhrasesServerErrorMapperCodes(): ServerErrorMapperCode[] {
    return [
        {
            code: 'DefectIds.CANNOT_BE_NULL',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-null');
            }
        },
        {
            code: 'DefectIds.CANNOT_CONTAIN_NULLS',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-null');
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.SEPARATE_DOT',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-separate-dot', {
                    minusWords: get(err, 'params.subset', '')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.WRONG_QUOTES',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-wrong-quote', {
                    minusWords: get(err, 'params.subset', '')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.IndividualKeywordLength.MAX_LENGTH_MINUS_WORD',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-max-length', {
                    minusWords: get(err, 'params.subset', []).join(', '),
                    maxLength: get(err, 'params.maxLength', '')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.IndividualKeywordLength.MAX_COUNT_WORDS_IN_KEYWORD',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-max-count-words-in-keyword', {
                    minusWords: get(err, 'params.subset', []).join(', '),
                    maxWords: get(err, 'params.maxWords', '')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.IMBALANCED_SQUARE_BRACKETS',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-imbalanced-square-brackets', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.NESTED_OR_EMPTY_SQUARE_BRACKETS',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-nested-or-empty-square-brackets', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_OPERATORS_INSIDE_SQUARE_BRACKETS',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-invalid-operators-inside-square-brackets', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_EXCLAMATION_MARK',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-invalid-exclamation-mark', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_MINUS_MARK',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-invalid-minus-mark', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_PLUS_MARK',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-invalid-plus-mark', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_WORD_FIRST_CHARACTER',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-invalid-word-first-character', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_DOT',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-invalid-dot', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.MinusPhrase.ILLEGAL_MINUS_KEYWORD_CHARS',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-illegal-minus-keyword-chars', {
                    minusWords: get(err, 'params.subset', []).join(', ')
                });
            }
        },
        {
            code: 'MinusPhraseDefectIds.StringLength.MAX_LENGTH_MINUS_KEYWORDS',
            message: (err: ValidationResultItem): string => {
                return i18n('minus-keywords-max-length-minus-keywords', {
                    maxLength: get(err, 'params.maxLength', '')
                });
            }
        }
    ];
}

export function parseStringToMinusPhrases(input: string): string[] {
    return input
        .split(/\s+?\-+|^\-+|,\-+|[,\n]/)
        .map(item => item.trim())
        .filter(Boolean);
}

export function mapMinusPhraseToString(phrase: string): string {
    return `-${phrase}`;
}

export function getInputStringFromMinusPhrasesList(minusPhrases: string[] = [], view: KeywordsEditorBodyView): string {
    const delimiter = view === KeywordsEditorBodyView.short ? '\n' : ', ';

    return minusPhrases.map(mapMinusPhraseToString).join(delimiter);
}

const initialMinusPhraseEditorState: MinusPhraseLibraryEditorData = {
    minusKeywordsEditor: {
        value: [],
        errors: [],
        isOpen: false,
        selectedTab: MinusPhrasesEditorTabs.libraryLists,
        view: KeywordsEditorBodyView.full
    }
};

export const getInitialMinusPhraseEditorState = (): MinusPhraseLibraryEditorData => initialMinusPhraseEditorState;

const initialMinusPhraseLibraryEditorState: MinusPhraseLibraryEditorState = {
    data: {
        [MassActionId.groupsEdit]: {
            minusKeywordsEditor: {
                ...getInitialMinusPhraseEditorState().minusKeywordsEditor,
                isOpen: true
            }
        }
    }
};

export const getInitialMinusPhraseLibraryEditorState = (): MinusPhraseLibraryEditorState =>
    initialMinusPhraseLibraryEditorState;
